import React, { useMemo, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  HiArrowSmRight,
  HiCheck,
  HiMenuAlt2,
  HiOutlineGlobe,
  HiOutlineRefresh,
  HiOutlineStatusOnline,
  HiX,
  HiOutlineChevronRight,
} from 'react-icons/hi';
import styled, { keyframes } from 'styled-components';
import {
  Avatar,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { useOverlayTriggerState } from 'react-stately';
import { useLocation, useParams } from 'react-router-dom';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';

import type { TransportMode } from 'src/cqrs/client';
import { NavLink, UnstyledLink } from './Link';
import CozifyLogo from '../../images/cozify_logo.svg?react';
import SwitchTenantModal from './modal/SwitchTenantModal';
import Sidebar from '../admin/Sidebar';
import { useOptions } from '~contexts/OptionsContext';
import { useSubscriptions } from '~hooks/useSubscriptions';
import { useActiveTenant, useTenants } from '~hooks/useTenant';
import LocaleFlag from '~components/common/LocaleFlag';
import { useI18n, Locale } from '~services/i18n';
import { useTransportStatus } from '~hooks/useTransportStatus';
import { theme } from '~constants/theme';
import type { Color } from '~constants/theme';
import { useAuth } from '~services/auth';
import { Icon, Spacer, Stack, Text } from '~uikit';
import { activeOpacity, hoverHighlight } from '~utils/styled';

// Connection indicator: green when the SignalR hub is up, yellow on HTTP
// (forced or auto-fallback), red while connecting with no fallback.
const STATUS_COLOR: Record<string, Color> = {
  connected: 'success',
  http: 'warn',
  connecting: 'error',
};

const SidebarDrawer = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const location = useLocation();
  const treeVisible = location.pathname === '/admin';
  const { data: subscriptions = [] } = useSubscriptions();
  const routeParams = useParams();

  const subscription = useMemo(() => {
    const id = routeParams.subscriptionId;
    return subscriptions.find(s => s.subscriptionId === id);
  }, [routeParams.subscriptionId, subscriptions]);

  return (
    <DrawerWrapper>
      <IconButton size="medium" onClick={() => setIsDrawerOpen(true)}>
        <Icon icon={HiMenuAlt2} size="normal" color="text" aria-hidden="true" />
      </IconButton>
      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        slotProps={{
          paper: {
            sx: { width: '90%', backgroundColor: theme.colors.muted5 },
          },
        }}
      >
        <DrawerHeader>
          <LogoWrapper>
            <CozifyLogo fill={theme.colors.text} />
          </LogoWrapper>
          <IconButton size="medium" onClick={() => setIsDrawerOpen(false)}>
            <Icon icon={HiX} size="normal" color="text" />
          </IconButton>
        </DrawerHeader>
        <DrawerNavLink
          to={'/dashboard'}
          testId={'drawerOverview'}
          preloadOn="hover"
          onClick={() => setIsDrawerOpen(false)}
        >
          <Trans>Overview</Trans>
        </DrawerNavLink>
        <DrawerNavLink
          to={'/sites'}
          testId={'drawerSites'}
          preloadOn="hover"
          onClick={() => setIsDrawerOpen(false)}
        >
          <Trans>Sites</Trans>
        </DrawerNavLink>
        <DrawerNavLink
          to={'/devices'}
          testId={'drawerDevices'}
          preloadOn="hover"
          onClick={() => setIsDrawerOpen(false)}
        >
          <Trans>Devices</Trans>
        </DrawerNavLink>
        <DrawerNavLink
          to={'/admin'}
          testId={'drawerAdmin'}
          preloadOn="hover"
          onClick={() => setIsDrawerOpen(false)}
        >
          <Trans>Admin</Trans>
        </DrawerNavLink>
        <SidebarTreeWrapper visible={treeVisible}>
          {subscription && <Sidebar mobile />}
          {!subscription &&
            subscriptions.map(s => {
              const navUrl = `/admin${s.id}`;
              return (
                <NavItem key={s.id}>
                  <SubscriptionNavItemLink to={navUrl} preloadOn="click">
                    <Stack axis="y" spacing="xxsmall" hideOverflow>
                      <Text
                        color={'text'}
                        variant={'bodyStrong'}
                        style={{
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {s.displayName}
                      </Text>
                      <Text
                        color={'text'}
                        variant="caption"
                        style={{
                          textTransform: 'uppercase',
                        }}
                      >
                        <Trans>Subscription</Trans>
                      </Text>
                    </Stack>
                    <Icon
                      icon={HiOutlineChevronRight}
                      size={'small'}
                      color="muted3"
                    />
                  </SubscriptionNavItemLink>
                </NavItem>
              );
            })}
        </SidebarTreeWrapper>
      </Drawer>
    </DrawerWrapper>
  );
};

type AvatarMenuProps = {
  user: string;
  tenant: string;
  hasMultipleTenants: boolean;
  onSwitchTenant: () => void;
  onLogout: () => void;
};

// The user menu is always visible (even when the top bar collapses) and hosts
// everything account-related: language, connection mode, tenant switch, logout.
function AvatarMenu({
  user,
  tenant,
  hasMultipleTenants,
  onSwitchTenant,
  onLogout,
}: AvatarMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const [connAnchor, setConnAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const closeAll = () => {
    setAnchorEl(null);
    setLangAnchor(null);
    setConnAnchor(null);
  };

  const { i18n, changeLocale } = useI18n();
  const { transportMode, setTransportMode } = useOptions();
  const transportStatus = useTransportStatus();

  // No avatar image is available, so show the user's initials instead of
  // requesting a (missing) /avatar.jpg, which would 404.
  const initials =
    String(user ?? '')
      .split('@')[0]
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s: string) => s[0]?.toUpperCase())
      .join('') || '?';

  const locales: { locale: Locale; label: string }[] = [
    { locale: 'fi', label: 'Suomi' },
    { locale: 'en', label: 'English' },
  ];
  const connections: { mode: TransportMode; label: string }[] = [
    { mode: 'auto', label: t`Automatic` },
    { mode: 'signalr', label: t`Direct` },
    { mode: 'http', label: t`HTTP` },
  ];
  const activeLocale = locales.find(l => l.locale === i18n.locale);
  const activeConnection = connections.find(c => c.mode === transportMode);

  // The avatar menu hugs the top-right edge, so open submenus to the left of
  // the row (right edge of the submenu meets the left edge of the item) to
  // avoid overflowing the viewport / overlapping the parent menu.
  const subMenuProps = {
    anchorOrigin: { vertical: 'top', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
  } as const;

  return (
    <>
      <IconButton onClick={e => setAnchorEl(e.currentTarget)} size="large">
        <Avatar>{initials}</Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeAll}
        // Drop the list's default top padding and clip the paper so the gray
        // header reaches the rounded top edge — otherwise the white paper shows
        // as a strip above the header.
        slotProps={{
          paper: { sx: { overflow: 'hidden' } },
          list: { sx: { pt: 0 } },
        }}
      >
        <MenuHeader>
          <Stack axis="y" spacing="xsmall">
            <Text variant="caption">
              <Trans>Logged in as</Trans>
            </Text>
            <Text variant="bodySmallStrong">{user ?? ''}</Text>
            <Text variant="caption">{tenant ?? ''}</Text>
          </Stack>
        </MenuHeader>

        <MenuItem onClick={e => setLangAnchor(e.currentTarget)}>
          <SubmenuRow>
            <ItemWrapper>
              <Icon icon={HiOutlineGlobe} size="xsmall" color="text" />
              <Spacer axis="x" size="small" />
              <Text variant="body">
                <Trans>Language</Trans>
              </Text>
            </ItemWrapper>
            <ItemWrapper>
              <Text variant="bodySmall" color="muted1">
                {activeLocale?.label}
              </Text>
              <Spacer axis="x" size="xxsmall" />
              <Icon icon={HiOutlineChevronRight} size="xsmall" color="muted3" />
            </ItemWrapper>
          </SubmenuRow>
        </MenuItem>

        <MenuItem onClick={e => setConnAnchor(e.currentTarget)}>
          <SubmenuRow>
            <ItemWrapper>
              <Icon
                icon={HiOutlineStatusOnline}
                size="xsmall"
                color={STATUS_COLOR[transportStatus] ?? 'muted1'}
              />
              <Spacer axis="x" size="small" />
              <Text variant="body">
                <Trans>Connection</Trans>
              </Text>
            </ItemWrapper>
            <ItemWrapper>
              <Text variant="bodySmall" color="muted1">
                {activeConnection?.label}
              </Text>
              <Spacer axis="x" size="xxsmall" />
              <Icon icon={HiOutlineChevronRight} size="xsmall" color="muted3" />
            </ItemWrapper>
          </SubmenuRow>
        </MenuItem>

        <Divider />

        {hasMultipleTenants && (
          <MenuItem
            onClick={() => {
              onSwitchTenant();
              closeAll();
            }}
          >
            <ItemWrapper>
              <Icon icon={HiOutlineRefresh} size="xsmall" color="text" />
              <Spacer axis="x" size="small" />
              <Text variant="body">
                <Trans>Switch tenant</Trans>
              </Text>
            </ItemWrapper>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            onLogout();
            closeAll();
          }}
        >
          <ItemWrapper>
            <Icon icon={HiArrowSmRight} size="xsmall" color="text" />
            <Spacer axis="x" size="small" />
            <Text variant="body">
              <Trans>Logout</Trans>
            </Text>
          </ItemWrapper>
        </MenuItem>
      </Menu>

      {/* Language submenu */}
      <Menu
        anchorEl={langAnchor}
        open={Boolean(langAnchor)}
        onClose={() => setLangAnchor(null)}
        {...subMenuProps}
      >
        {locales.map(l => (
          <MenuItem
            key={l.locale}
            selected={i18n.locale === l.locale}
            onClick={() => {
              changeLocale(l.locale);
              closeAll();
            }}
          >
            <ItemWrapper>
              <LocaleFlag locale={l.locale} />
              <Spacer axis="x" size="small" />
              <Text variant="body">{l.label}</Text>
              {i18n.locale === l.locale && (
                <>
                  <Spacer axis="x" size="small" />
                  <Icon icon={HiCheck} size="xsmall" color="primary" />
                </>
              )}
            </ItemWrapper>
          </MenuItem>
        ))}
      </Menu>

      {/* Connection submenu */}
      <Menu
        anchorEl={connAnchor}
        open={Boolean(connAnchor)}
        onClose={() => setConnAnchor(null)}
        {...subMenuProps}
      >
        {connections.map(c => (
          <MenuItem
            key={c.mode}
            selected={transportMode === c.mode}
            onClick={() => {
              setTransportMode(c.mode);
              closeAll();
            }}
          >
            <ItemWrapper>
              <Icon
                icon={HiOutlineStatusOnline}
                size="xsmall"
                color={
                  transportMode === c.mode
                    ? (STATUS_COLOR[transportStatus] ?? 'muted1')
                    : 'muted3'
                }
              />
              <Spacer axis="x" size="small" />
              <Text variant="body">{c.label}</Text>
              {transportMode === c.mode && (
                <>
                  <Spacer axis="x" size="small" />
                  <Icon icon={HiCheck} size="xsmall" color="primary" />
                </>
              )}
            </ItemWrapper>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                                MAIN TOOLBAR                                 */
/* -------------------------------------------------------------------------- */

export default function Toolbar() {
  const auth = useAuth();
  const tenantModalState = useOverlayTriggerState({});

  // Global data-activity indicator. All service I/O goes through React Query:
  // queries (incl. CQRS graph reads over SignalR/HTTP) show as `isFetching`,
  // commands/mutations (incl. CQRS commands over SignalR) as `isMutating`.
  // Either one active drives the marquee line along the bottom edge of the bar.
  const isBusy = useIsFetching() + useIsMutating() > 0;

  const { data: activeTenant } = useActiveTenant();
  const tenantDisplayName = activeTenant?.displayName ?? '-';

  // Only offer "Switch tenant" when the user actually has more than one.
  const { data: tenants } = useTenants();
  const hasMultipleTenants = (tenants?.data?.length ?? 0) > 1;

  const pages = [
    { label: t`Overview`, to: '/dashboard' },
    { label: t`Sites`, to: '/sites' },
    { label: t`Devices`, to: `/devices` },
    { label: t`Admin`, to: '/admin' },
  ];

  return (
    <Wrapper>
      <SidebarDrawer />

      {tenantModalState.isOpen && (
        <SwitchTenantModal state={tenantModalState} />
      )}

      <Stack axis="x" spacing="small" align="center">
        <LogoLink to="/" aria-label={t`Home`}>
          <LogoWrapper>
            <CozifyLogo fill={theme.colors.text} />
          </LogoWrapper>
        </LogoLink>

        {pages.map(page => (
          <TopNavItem key={page.label}>
            <NavItemLink to={page.to}>
              <Text variant="body">{page.label}</Text>
            </NavItemLink>
          </TopNavItem>
        ))}
      </Stack>

      <OptionsWrapper>
        <AvatarMenu
          user={auth.userName()}
          tenant={tenantDisplayName}
          hasMultipleTenants={hasMultipleTenants}
          onSwitchTenant={() => tenantModalState.open()}
          onLogout={auth.logout}
        />
      </OptionsWrapper>

      <LoadingBar
        $active={isBusy}
        aria-hidden="true"
        data-testid="global-loading-bar"
      >
        <LoadingBarSegment />
      </LoadingBar>
    </Wrapper>
  );
}

const Wrapper = styled.header`
  position: relative; /* anchors the global loading bar to the bottom edge */
  height: 5rem; /* Fixed height for calc() math */
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${p => p.theme.spacing.normal}px;
  background-color: ${p => p.theme.colors.elevated};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  z-index: 1000;
`;

// Indeterminate "marquee" segment that sweeps across the bottom border while
// data is loading. Left → right on a loop; the fade (opacity) is on the track.
const marquee = keyframes`
  0%   { left: -45%; }
  100% { left: 100%; }
`;

// Full-width track pinned over the bottom border; fades in/out with fetch
// activity so a quick fetch doesn't flash harshly.
const LoadingBar = styled.div<{ $active: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px; /* sit on top of the 1px border-bottom */
  height: 2px;
  overflow: hidden;
  pointer-events: none;
  opacity: ${p => (p.$active ? 1 : 0)};
  transition: opacity 200ms ease;
`;

const LoadingBarSegment = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 45%;
  background: linear-gradient(
    90deg,
    transparent,
    ${p => p.theme.colors.primary} 45%,
    ${p => p.theme.colors.primary} 55%,
    transparent
  );
  animation: ${marquee} 1.1s ease-in-out infinite;
`;

const NavItemLink = styled(NavLink)`
  position: relative;
  padding: ${p => p.theme.spacing.small}px ${p => p.theme.spacing.normal}px;
  color: ${p => p.theme.colors.text};
  ${hoverHighlight}
  ${activeOpacity}

  &[class*='active'] {
    text-decoration: solid underline ${p => p.theme.colors.primary} 3px;
    text-underline-offset: 0.8rem;
    font-weight: bold;
  }
`;

// Keep the logo its own (black) color — anchors otherwise inherit the UA link
// color, which bleeds into the logo's currentColor paths.
const LogoLink = styled(UnstyledLink)`
  display: flex;
  color: ${p => p.theme.colors.text};
`;

const LogoWrapper = styled.div`
  width: 120px;
  border-radius: ${p => p.theme.radii.small}px;
  margin: 0px ${p => p.theme.spacing.small}px;
  display: flex;
  justify-content: center;
`;

const OptionsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DrawerWrapper = styled.div`
  display: none;
  ${p => p.theme.media.tabletDown} {
    display: block;
  }
`;

const DrawerHeader = styled.div`
  padding: ${p => p.theme.spacing.normal}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const DrawerNavLink = styled(NavLink)`
  padding: 16px;
  display: block;
  color: ${p => p.theme.colors.text};
  &[class*='active'] {
    background: ${p => p.theme.colors.primary}10;
    border-left: 4px solid ${p => p.theme.colors.primary};
  }
`;

const TopNavItem = styled.li`
  list-style: none;
  ${p => p.theme.media.tabletDown} {
    display: none;
  }
`;

const NavItem = styled.li`
  list-style: none;
`;

const SubscriptionNavItemLink = styled(UnstyledLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${p => p.theme.colors.text};
  padding: ${p => p.theme.spacing.small}px ${p => p.theme.spacing.normal}px;
  ${activeOpacity}
`;

const MenuHeader = styled.div`
  padding: 16px;
  background: ${p => p.theme.colors.muted5};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const SubmenuRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing.large}px;
  width: 100%;
`;

const ItemWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SidebarTreeWrapper = styled.aside<{ visible?: boolean }>`
  display: ${p => (p.visible ? 'block' : 'none')};
  height: 100%;
`;
